/**
 * Conversion Intelligence Engine
 * 
 * Lead scoring, appointment conversion tracking, follow-up effectiveness,
 * rep performance, and source performance analytics.
 */

import type { Opportunity } from "./opportunityTypes";
import type { FollowUpTask } from "./opportunityTypes";
import type { Conversation, Message } from "./communicationTypes";

/* ═══════ LEAD SCORING ═══════ */

export type LeadTemperature = "hot" | "warm" | "cold";

export interface LeadScoreResult {
  score: number;        // 0–100
  temperature: LeadTemperature;
  factors: { name: string; points: number; max: number }[];
}

const HOT_SOURCES = ["Google Ads", "Facebook Ads", "Angi", "HomeAdvisor"];
const WARM_SOURCES = ["Referral", "Website", "Home Show"];

export function scoreLeadConversion(
  lead: Opportunity,
  conversations: Conversation[],
  messages: Message[],
): LeadScoreResult {
  const factors: { name: string; points: number; max: number }[] = [];

  // 1. Source quality (0–25)
  const src = lead.leadSource || "";
  const srcPts = HOT_SOURCES.includes(src) ? 25 : WARM_SOURCES.includes(src) ? 15 : 5;
  factors.push({ name: "Source Quality", points: srcPts, max: 25 });

  // 2. Responsiveness / engagement (0–25)
  const convo = conversations.find((c) => c.linkedLeadId === lead.id || c.linkedQuoteId === lead.id);
  const msgs = convo ? messages.filter((m) => m.conversationId === convo.id) : [];
  const inbound = msgs.filter((m) => m.direction === "inbound").length;
  const outbound = msgs.filter((m) => m.direction === "outbound").length;
  const engagePts = Math.min(25, inbound * 8 + (outbound > 0 ? 5 : 0));
  factors.push({ name: "Engagement", points: engagePts, max: 25 });

  // 3. Timeline urgency (0–25)
  const ageMs = Date.now() - new Date(lead.createdAt).getTime();
  const ageHrs = ageMs / (1000 * 60 * 60);
  const urgPts = ageHrs < 1 ? 25 : ageHrs < 4 ? 20 : ageHrs < 24 ? 15 : ageHrs < 72 ? 8 : 3;
  factors.push({ name: "Urgency", points: urgPts, max: 25 });

  // 4. Value signal (0–25)
  const val = lead.totalEstimate || 0;
  const valPts = val >= 25000 ? 25 : val >= 15000 ? 20 : val >= 8000 ? 12 : val > 0 ? 6 : 2;
  factors.push({ name: "Value Signal", points: valPts, max: 25 });

  const score = Math.min(100, factors.reduce((s, f) => s + f.points, 0));
  const temperature: LeadTemperature = score >= 65 ? "hot" : score >= 35 ? "warm" : "cold";

  return { score, temperature, factors };
}

export const TEMP_CONFIG: Record<LeadTemperature, { label: string; color: string; bg: string; icon: string }> = {
  hot:  { label: "Hot",  color: "text-destructive",      bg: "bg-destructive/10 border-destructive/20", icon: "🔥" },
  warm: { label: "Warm", color: "text-warning",        bg: "bg-warning/10 border-warning/30",             icon: "☀️" },
  cold: { label: "Cold", color: "text-muted-foreground", bg: "bg-muted border-muted",                    icon: "❄️" },
};

/* ═══════ APPOINTMENT CONVERSION TRACKING ═══════ */

export interface ConversionFunnel {
  booked: number;
  completed: number;
  sold: number;
  noShow: number;
  noSale: number;
  followUpCreated: number;
  bookToCompleteRate: number;
  completeToSoldRate: number;
  overallConversionRate: number;
  showRate: number;
  noSaleToFollowUpRate: number;
}

export function calculateConversionFunnel(
  opportunities: Opportunity[],
  followUpTasks: FollowUpTask[],
): ConversionFunnel {
  const withAppt = opportunities.filter((o) => o.appointmentTime || o.stage !== "Lead");
  const booked = withAppt.length;
  const noShow = withAppt.filter((o) => o.disposition?.reason === "no_show" || o.disposition?.reason === "not_home").length;
  const completed = withAppt.filter((o) =>
    o.stage === "Appointment Completed" || o.stage === "Follow-Up Needed" ||
    o.stage === "Closed Won" || o.stage === "Closed Lost"
  ).length;
  const sold = withAppt.filter((o) => o.stage === "Closed Won").length;
  const noSale = completed - sold;
  const noSaleOpps = withAppt.filter((o) => o.stage === "Closed Lost" || o.stage === "Follow-Up Needed");
  const followUpCreated = noSaleOpps.filter((o) =>
    followUpTasks.some((t) => t.opportunityId === o.id)
  ).length;

  return {
    booked, completed, sold, noShow, noSale, followUpCreated,
    bookToCompleteRate: booked > 0 ? Math.round((completed / booked) * 100) : 0,
    completeToSoldRate: completed > 0 ? Math.round((sold / completed) * 100) : 0,
    overallConversionRate: booked > 0 ? Math.round((sold / booked) * 100) : 0,
    showRate: booked > 0 ? Math.round(((booked - noShow) / booked) * 100) : 100,
    noSaleToFollowUpRate: noSale > 0 ? Math.round((followUpCreated / noSale) * 100) : 0,
  };
}

/* ═══════ CONVERSION BY REP ═══════ */

export interface RepConversionMetrics {
  rep: string;
  booked: number;
  completed: number;
  sold: number;
  noShow: number;
  closeRate: number;
  showRate: number;
  totalRevenue: number;
  avgDealSize: number;
  followUpCompletionRate: number;
  avgResponseTimeSec: number | null;
}

export function getRepConversionMetrics(
  opportunities: Opportunity[],
  followUpTasks: FollowUpTask[],
  conversations: Conversation[],
  messages: Message[],
): RepConversionMetrics[] {
  const repMap = new Map<string, Opportunity[]>();
  opportunities.filter((o) => o.appointmentTime || o.stage !== "Lead").forEach((o) => {
    const rep = o.assignedRep || "Unassigned";
    if (!repMap.has(rep)) repMap.set(rep, []);
    repMap.get(rep)!.push(o);
  });

  return Array.from(repMap.entries()).map(([rep, opps]) => {
    const booked = opps.length;
    const noShow = opps.filter((o) => o.disposition?.reason === "no_show" || o.disposition?.reason === "not_home").length;
    const completed = opps.filter((o) =>
      o.stage === "Appointment Completed" || o.stage === "Follow-Up Needed" ||
      o.stage === "Closed Won" || o.stage === "Closed Lost"
    ).length;
    const sold = opps.filter((o) => o.stage === "Closed Won").length;
    const totalRevenue = opps.filter((o) => o.stage === "Closed Won").reduce((s, o) => s + (o.totalEstimate || 0), 0);

    // Follow-up completion rate
    const repTasks = followUpTasks.filter((t) => t.assignedTo === rep);
    const completedTasks = repTasks.filter((t) => t.status === "completed").length;
    const fuRate = repTasks.length > 0 ? Math.round((completedTasks / repTasks.length) * 100) : 0;

    // Response time
    const responseTimes: number[] = [];
    opps.forEach((o) => {
      const convo = conversations.find((c) => c.linkedLeadId === o.id || c.linkedQuoteId === o.id);
      if (!convo) return;
      const outbound = messages
        .filter((m) => m.conversationId === convo.id && m.direction === "outbound")
        .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      if (outbound.length > 0) {
        const diff = (new Date(outbound[0].timestamp).getTime() - new Date(o.createdAt).getTime()) / 1000;
        if (diff > 0) responseTimes.push(diff);
      }
    });
    const avgResp = responseTimes.length > 0 ? Math.round(responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length) : null;

    return {
      rep, booked, completed, sold, noShow,
      closeRate: completed > 0 ? Math.round((sold / completed) * 100) : 0,
      showRate: booked > 0 ? Math.round(((booked - noShow) / booked) * 100) : 100,
      totalRevenue, avgDealSize: sold > 0 ? Math.round(totalRevenue / sold) : 0,
      followUpCompletionRate: fuRate,
      avgResponseTimeSec: avgResp,
    };
  }).sort((a, b) => b.totalRevenue - a.totalRevenue);
}

/* ═══════ CONVERSION BY SOURCE ═══════ */

export interface SourceConversionMetrics {
  source: string;
  totalLeads: number;
  appointments: number;
  sold: number;
  totalRevenue: number;
  costPerLead: number;
  costPerAppointment: number;
  costPerSale: number;
  leadToApptRate: number;
  apptToSaleRate: number;
  overallRate: number;
  avgDealSize: number;
  roi: number;
}

export function getSourceConversionMetrics(
  opportunities: Opportunity[],
): SourceConversionMetrics[] {
  const srcMap = new Map<string, Opportunity[]>();
  opportunities.forEach((o) => {
    const src = o.leadSource || "Unknown";
    if (!srcMap.has(src)) srcMap.set(src, []);
    srcMap.get(src)!.push(o);
  });

  return Array.from(srcMap.entries()).map(([source, opps]) => {
    const totalLeads = opps.length;
    const appointments = opps.filter((o) => o.appointmentTime || o.stage !== "Lead").length;
    const sold = opps.filter((o) => o.stage === "Closed Won").length;
    const totalRevenue = opps.filter((o) => o.stage === "Closed Won").reduce((s, o) => s + (o.totalEstimate || 0), 0);

    // Estimate marketing cost from seed data conventions
    const estCostPerLead = source === "Google Ads" ? 85 : source === "Facebook Ads" ? 65 :
      source === "Angi" ? 120 : source === "HomeAdvisor" ? 110 :
      source === "Referral" ? 0 : source === "Home Show" ? 40 :
      source === "Direct Mail" ? 30 : source === "TV Ad" ? 150 : 50;
    const totalCost = totalLeads * estCostPerLead;

    return {
      source, totalLeads, appointments, sold, totalRevenue,
      costPerLead: estCostPerLead,
      costPerAppointment: appointments > 0 ? Math.round(totalCost / appointments) : 0,
      costPerSale: sold > 0 ? Math.round(totalCost / sold) : 0,
      leadToApptRate: totalLeads > 0 ? Math.round((appointments / totalLeads) * 100) : 0,
      apptToSaleRate: appointments > 0 ? Math.round((sold / appointments) * 100) : 0,
      overallRate: totalLeads > 0 ? Math.round((sold / totalLeads) * 100) : 0,
      avgDealSize: sold > 0 ? Math.round(totalRevenue / sold) : 0,
      roi: totalCost > 0 ? Math.round(((totalRevenue - totalCost) / totalCost) * 100) : 0,
    };
  }).sort((a, b) => b.totalRevenue - a.totalRevenue);
}

/* ═══════ FOLLOW-UP EFFECTIVENESS ═══════ */

export interface FollowUpEffectiveness {
  totalFollowUps: number;
  completed: number;
  convertedToAppointment: number;
  convertedToSale: number;
  followUpToApptRate: number;
  followUpToSaleRate: number;
  completionRate: number;
  avgTimeToCompleteHrs: number;
}

export function calculateFollowUpEffectiveness(
  followUpTasks: FollowUpTask[],
  opportunities: Opportunity[],
): FollowUpEffectiveness {
  const total = followUpTasks.length;
  const completed = followUpTasks.filter((t) => t.status === "completed").length;

  // Follow-ups that led to re-engagement
  const convertedToAppointment = followUpTasks.filter((t) => {
    if (t.status !== "completed") return false;
    const opp = opportunities.find((o) => o.id === t.opportunityId);
    return opp && (opp.stage === "Appointment Scheduled" || opp.stage === "Appointment Completed");
  }).length;

  const convertedToSale = followUpTasks.filter((t) => {
    if (t.status !== "completed") return false;
    const opp = opportunities.find((o) => o.id === t.opportunityId);
    return opp && opp.stage === "Closed Won";
  }).length;

  // Avg time to complete
  const completedWithTime = followUpTasks.filter((t) => t.status === "completed" && t.completedAt);
  const avgHrs = completedWithTime.length > 0
    ? Math.round(completedWithTime.reduce((s, t) => {
        const diff = (new Date(t.completedAt!).getTime() - new Date(t.createdAt).getTime()) / (1000 * 60 * 60);
        return s + diff;
      }, 0) / completedWithTime.length)
    : 0;

  return {
    totalFollowUps: total, completed,
    convertedToAppointment, convertedToSale,
    followUpToApptRate: completed > 0 ? Math.round((convertedToAppointment / completed) * 100) : 0,
    followUpToSaleRate: completed > 0 ? Math.round((convertedToSale / completed) * 100) : 0,
    completionRate: total > 0 ? Math.round((completed / total) * 100) : 0,
    avgTimeToCompleteHrs: avgHrs,
  };
}
